import { Injectable, signal } from '@angular/core';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  message: string;
  type: NotificationType;
  duration?: number;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly notificationsSignal = signal<Notification[]>([]);
  private notificationCounter = 0;

  readonly notifications = this.notificationsSignal.asReadonly();

  /**
   * Shows a notification toast
   * @param message - The message to display
   * @param type - The type of notification (success, error, warning, info)
   * @param duration - Duration in milliseconds before auto-dismiss (default: 5000ms)
   */
  showNotification(message: string, type: NotificationType = 'info', duration: number = 5000): void {
    const id = `notification-${++this.notificationCounter}`;
    const notification: Notification = { id, message, type, duration };

    // Add notification to the list
    this.notificationsSignal.update((notifications) => [...notifications, notification]);

    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => {
        this.removeNotification(id);
      }, duration);
    }
  }

  /**
   * Shows a success notification
   */
  showSuccess(message: string, duration: number = 5000): void {
    this.showNotification(message, 'success', duration);
  }

  /**
   * Shows an error notification
   */
  showError(message: string, duration: number = 5000): void {
    this.showNotification(message, 'error', duration);
  }

  /**
   * Shows a warning notification
   */
  showWarning(message: string, duration: number = 5000): void {
    this.showNotification(message, 'warning', duration);
  }

  /**
   * Shows an info notification
   */
  showInfo(message: string, duration: number = 5000): void {
    this.showNotification(message, 'info', duration);
  }

  /**
   * Shows a session expired notification
   */
  showSessionExpired(): void {
    this.showError('Your session has expired. Please log in again.', 6000);
  }

  /**
   * Removes a specific notification by ID
   */
  removeNotification(id: string): void {
    this.notificationsSignal.update((notifications) =>
      notifications.filter((notification) => notification.id !== id)
    );
  }

  /**
   * Clears all notifications
   */
  clearAll(): void {
    this.notificationsSignal.set([]);
  }
}
